/**
 * MentionTextarea — a Textarea with @mention autocomplete.
 *
 * When the user types `@`, a dropdown appears with matching users.
 * Selecting a user inserts their name and tracks their ID.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  makeStyles,
  Textarea,
  tokens,
} from '@fluentui/react-components'
import type { MentionableUser } from '../hooks/useMentionableUsers'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  dropdown: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    right: 0,
    maxHeight: '180px',
    overflowY: 'auto',
    background: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow8,
    zIndex: 100,
    marginBottom: '4px',
  },
  item: {
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
    },
  },
  itemActive: {
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: tokens.colorBrandBackground2,
  },
  avatar: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: bayerColors.navy,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 700,
    flexShrink: 0,
  },
  name: {
    fontWeight: 500,
  },
  role: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
  },
  empty: {
    padding: '8px 10px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
})

export interface MentionTextareaProps {
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  mentionableUsers: MentionableUser[]
  mentionedIds: number[]
  onMentionedIdsChange: (ids: number[]) => void
  placeholder?: string
  rows?: number
  ariaLabel?: string
  disabled?: boolean
}

export function MentionTextarea({
  value,
  onChange,
  onKeyDown,
  mentionableUsers,
  mentionedIds,
  onMentionedIdsChange,
  placeholder,
  rows = 2,
  ariaLabel,
  disabled,
}: MentionTextareaProps) {
  const styles = useStyles()
  const [showMenu, setShowMenu] = useState(false)
  const [filter, setFilter] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [mentionStart, setMentionStart] = useState<number | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const filtered = mentionableUsers.filter((u) =>
    u.name.toLowerCase().includes(filter.toLowerCase()),
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [filter])

  function getNativeTextarea(): HTMLTextAreaElement | null {
    return wrapperRef.current?.querySelector('textarea') ?? null
  }

  const insertMention = useCallback(
    (user: MentionableUser) => {
      if (mentionStart === null) return
      const before = value.slice(0, mentionStart)
      const after = value.slice(mentionStart + filter.length + 1) // +1 for @
      const newValue = `${before}@${user.name} ${after}`
      onChange(newValue)
      if (!mentionedIds.includes(user.id)) {
        onMentionedIdsChange([...mentionedIds, user.id])
      }
      setShowMenu(false)
      setFilter('')
      setMentionStart(null)
      setTimeout(() => getNativeTextarea()?.focus(), 0)
    },
    [value, mentionStart, filter, mentionedIds, onChange, onMentionedIdsChange],
  )

  function handleChange(
    _e: React.ChangeEvent<HTMLTextAreaElement>,
    data: { value: string },
  ) {
    const newValue = data.value
    onChange(newValue)

    const textarea = getNativeTextarea()
    if (!textarea) return
    const pos = textarea.selectionStart
    const textBefore = newValue.slice(0, pos)

    const atMatch = textBefore.match(/(^|[\s])@([^\s]*)$/)
    if (atMatch) {
      const startIndex = textBefore.lastIndexOf('@')
      setMentionStart(startIndex)
      setFilter(atMatch[2] ?? '')
      setShowMenu(true)
    } else {
      setShowMenu(false)
      setFilter('')
      setMentionStart(null)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (showMenu && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % filtered.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const user = filtered[activeIndex]
        if (user) insertMention(user)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShowMenu(false)
        return
      }
    }
    onKeyDown?.(e)
  }

  function getInitials(name: string) {
    return name
      .split(' ')
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {showMenu && filtered.length > 0 && (
        <div className={styles.dropdown} data-testid="mention-dropdown">
          {filtered.map((user, i) => (
            <div
              key={user.id}
              className={i === activeIndex ? styles.itemActive : styles.item}
              onMouseDown={(e) => {
                e.preventDefault()
                insertMention(user)
              }}
              data-testid={`mention-option-${user.id}`}
            >
              <span className={styles.avatar}>{getInitials(user.name)}</span>
              <span className={styles.name}>{user.name}</span>
              <span className={styles.role}>{user.role}</span>
            </div>
          ))}
        </div>
      )}
      {showMenu && filtered.length === 0 && (
        <div className={styles.dropdown}>
          <div className={styles.empty}>No matching users</div>
        </div>
      )}
      <Textarea
        rows={rows}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={disabled}
      />
    </div>
  )
}
